/**
 * Settings: units/theme/voice/camera toggles, route avoidances, vehicle
 * profiles with editable truck dimensions, and (admins only) the user-approval
 * section. Renders from the store; every control writes straight back to it.
 */

import { store } from '../core/store';
import { APP_VERSION } from '../core/config';
import { newVehicle, type Settings, type VehicleKind, type VehicleProfile } from '../core/settings';
import * as api from '../auth/api';
import type { PendingUser, User } from '../auth/api';
import { t } from '../core/i18n';
import { announce, chime } from '../audio/voice';
import { disablePush, enablePush, mirrorLangForWorker, pushEnabled, pushPermission } from '../pwa/push';
import { loadFerries, parseTimes, saveFerries } from '../nav/ferry';

const SPEAKER_ICON = `<svg viewBox="0 0 24 24"><path d="M4 9 h4 l5-4 v14 l-5-4 H4 Z M16.5 8.5 a5 5 0 0 1 0 7 M19.5 5.5 a9 9 0 0 1 0 13"
  fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
const BELL_ICON = `<svg viewBox="0 0 24 24"><path d="M6 17 V11 a6 6 0 0 1 12 0 v6 l2 2 H4 Z M10 21 h4"
  fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
const MUTE_ICON = `<svg viewBox="0 0 24 24"><path d="M4 9 h4 l5-4 v14 l-5-4 H4 Z M16 10 l5 5 M21 10 l-5 5"
  fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"/></svg>`;

/**
 * One of three, not a switch.
 *
 * The old control was a single "voice on/off" toggle, which forced a choice
 * between narration and silence. The middle option - a loud chime when
 * something actually needs doing - is what the driver asked for and is
 * probably what most people want on a road they already know.
 */
function soundModeButton(
  mode: string,
  current: string,
  label: string,
  hint: string,
  icon: string,
): string {
  const on = mode === current;
  return `
    <button class="sound-mode ${on ? 'is-on' : ''}" data-sound="${mode}"
            role="radio" aria-checked="${on}">
      <span class="sound-mode__icon">${icon}</span>
      <span class="sound-mode__label">${label}</span>
      <span class="sound-mode__hint">${hint}</span>
    </button>`;
}

export class SettingsPanel {
  readonly el: HTMLElement;
  /** Does THIS device hold a push subscription right now? */
  private pushOn = false;
  private adminUsers: PendingUser[] | null = null;

  constructor(
    parent: HTMLElement,
    private currentUser: () => User | null,
    private onClose: () => void,
    private onLogout: () => void,
  ) {
    this.el = document.createElement('div');
    this.el.className = 'panel panel--settings';
    this.el.hidden = true;
    parent.appendChild(this.el);
  }

  async open(): Promise<void> {
    this.el.hidden = false;
    this.render();
    // Read the real subscription state, then repaint - the switch must show
    // what the browser actually has, not what we last assumed.
    const wasOn = this.pushOn;
    this.pushOn = await pushEnabled();
    if (this.pushOn !== wasOn) this.render();
    if (this.currentUser()?.role === 'admin') {
      try {
        this.adminUsers = (await api.adminListUsers()).users;
      } catch {
        this.adminUsers = null;
      }
      if (!this.el.hidden) this.render();
    }
  }

  close(): void {
    this.el.hidden = true;
    this.adminUsers = null;
  }

  private render(): void {
    const s = store.get().settings;
    const user = this.currentUser();

    this.el.innerHTML = `
      <div class="panel__bar">
        <button class="icon-btn" data-act="close" aria-label="Back">
          <svg viewBox="0 0 24 24"><path d="M15 4 L7 12 L15 20" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/></svg>
        </button>
        <h2>${t('set.title')}</h2>
        <span class="panel__version">v${APP_VERSION}</span>
      </div>
      <div class="panel__scroll">

        <section class="set-group">
          <h3>${t('set.vehicle')}</h3>
          <div class="vehicle-cards">${s.vehicles.map((v) => this.vehicleCard(v, s)).join('')}</div>
          <div class="vehicle-add">
            <button class="btn btn--small btn--ghost" data-addvehicle="car">${t('set.vehicle.addCar')}</button>
            <button class="btn btn--small btn--ghost" data-addvehicle="truck">${t('set.vehicle.addTruck')}</button>
          </div>
        </section>

        <section class="set-group">
          <h3>${t('set.pace')}</h3>
          <div class="panel__hint panel__hint--tight">${t('set.pace.hint')}</div>
          ${this.speed('motorway', t('set.pace.motorway'), s.speeds.motorway)}
          ${this.speed('dual', t('set.pace.dual'), s.speeds.dual)}
          ${this.speed('single', t('set.pace.single'), s.speeds.single)}
          <label class="set-row">
            <span>${t('set.pace.signal')}</span>
            <select data-set="signalSeconds">
              ${[0, 30, 60, 90, 120, 150, 180, 240]
                .map(
                  (v) =>
                    `<option value="${v}" ${s.signalSeconds === v ? 'selected' : ''}>${
                      v === 0 ? t('set.overspeed.off') : `${v < 60 ? `${v} s` : `${v / 60} min`}`
                    }</option>`,
                )
                .join('')}
            </select>
          </label>
        </section>

        <section class="set-group">
          <h3>${t('set.route')}</h3>
          ${this.toggle('route.avoidTolls', t('preview.avoidTolls'), s.route.avoidTolls)}
          ${this.toggle('route.avoidMotorways', t('preview.avoidMotorways'), s.route.avoidMotorways)}
          ${this.toggle('route.avoidFerries', t('set.avoidFerries'), s.route.avoidFerries)}
          ${this.toggle('route.avoidUnpaved', t('set.avoidUnpaved'), s.route.avoidUnpaved)}
          ${this.toggle('route.useTraffic', t('set.useTraffic'), s.route.useTraffic)}
          ${this.toggle('route.alternatives', t('set.alternatives'), s.route.alternatives)}
          ${this.toggle('aiAdvice', t('set.ai'), s.aiAdvice)}
          <div class="panel__hint">${t('set.aiHint')}</div>
          <label class="set-row">
            <span>${t('set.overlay')}</span>
            <select data-set="drivenOverlay">
              <option value="off" ${s.drivenOverlay === 'off' ? 'selected' : ''}>${t('set.overlay.off')}</option>
              <option value="speed" ${s.drivenOverlay === 'speed' ? 'selected' : ''}>${t('set.overlay.speed')}</option>
              <option value="rough" ${s.drivenOverlay === 'rough' ? 'selected' : ''}>${t('set.overlay.rough')}</option>
            </select>
          </label>
          <label class="set-row">
            <span>${t('rough.sensitivity')}</span>
            <select data-set="roughSensitivity">
              ${[1, 2, 3, 4, 5]
                .map(
                  (v) =>
                    `<option value="${v}" ${s.roughSensitivity === v ? 'selected' : ''}>${v} - ${t(`rough.sens.${v}`)}</option>`,
                )
                .join('')}
            </select>
          </label>
          ${this.toggle('avoidRough', t('rough.avoid'), s.avoidRough)}
          <div class="panel__hint">${t('rough.avoidHint')}</div>
          ${this.toggle('breadcrumbTrail', t('set.crumbs'), s.breadcrumbTrail)}
        </section>

        <section class="set-group">
          <h3>${t('ferry.settings')}</h3>
          <div class="panel__hint panel__hint--tight">${t('ferry.settingsHint')}</div>
          ${this.ferriesHtml()}
        </section>

        <section class="set-group">
          <h3>${t('set.guidance')}</h3>
          <div class="set-row set-row--stack">
            <span>${t('sound.title')}</span>
            <div class="sound-modes" role="radiogroup" aria-label="${t('sound.title')}">
              ${soundModeButton('voice', s.soundMode, t('sound.voice'), t('sound.voiceHint'), SPEAKER_ICON)}
              ${soundModeButton('alerts', s.soundMode, t('sound.alerts'), t('sound.alertsHint'), BELL_ICON)}
              ${soundModeButton('muted', s.soundMode, t('sound.muted'), t('sound.mutedHint'), MUTE_ICON)}
            </div>
          </div>
          <label class="set-row ${s.soundMode === 'muted' ? 'is-disabled' : ''}">
            <span>${t('sound.volume')}</span>
            <span class="set-slider">
              <input type="range" min="0" max="150" step="5"
                     value="${Math.round(s.voiceVolume * 100)}" data-volume />
              <span class="set-slider__value">${Math.round(s.voiceVolume * 100)}%</span>
              <button class="btn btn--ghost btn--tiny" data-act="test-sound">${t('sound.test')}</button>
            </span>
          </label>
          ${this.toggle('duckMusic', t('set.duck'), s.duckMusic)}
          <label class="set-row">
            <span>${t('set.audioSource')}</span>
            <select data-set="audioSource">
              <option value="spotify" ${s.audioSource === 'spotify' ? 'selected' : ''}>Spotify</option>
              <option value="audible" ${s.audioSource === 'audible' ? 'selected' : ''}>Audible</option>
            </select>
          </label>
          <div class="panel__hint">${t('set.audioSourceHint')}</div>
          ${this.toggle('trafficWatch', t('set.trafficWatch'), s.trafficWatch)}
          ${this.toggle('clock24', t('set.clock24'), s.clock24)}
          ${this.toggle('adaptiveCamera', t('set.autozoom'), s.adaptiveCamera)}
          <label class="set-row">
            <span>${t('set.driveZoom')}</span>
            <span class="set-slider">
              <input type="range" min="0" max="8" step="1"
                     value="${s.driveZoom === 0 ? 0 : Math.min(8, Math.max(1, Math.round((s.driveZoom - 13.5) * 2)))}" data-drivezoom />
              <span class="set-slider__value">${s.driveZoom === 0 ? t('set.driveZoom.auto') : s.driveZoom.toFixed(1)}</span>
            </span>
          </label>
          <div class="panel__hint">${t('set.driveZoomHint')}</div>
          ${this.toggle('keepAwake', t('set.awake'), s.keepAwake)}
          ${this.pushRow()}
          <label class="set-row">
            <span>${t('set.overspeed')}</span>
            <select data-set="speedWarnOffset">
              ${[0, 5, 10, 15, 20]
                .map(
                  (v) =>
                    `<option value="${v}" ${s.speedWarnOffset === v ? 'selected' : ''}>${v === 0 ? t('set.overspeed.off') : `+${v} km/h`}</option>`,
                )
                .join('')}
            </select>
          </label>
        </section>

        <section class="set-group">
          <h3>${t('set.display')}</h3>
          <label class="set-row">
            <span>${t('set.language')}</span>
            <select data-set="language">
              <option value="en" ${s.language === 'en' ? 'selected' : ''}>English</option>
              <option value="th" ${s.language === 'th' ? 'selected' : ''}>ไทย</option>
            </select>
          </label>
          <label class="set-row">
            <span>${t('set.theme')}</span>
            <select data-set="theme">
              <option value="auto" ${s.theme === 'auto' ? 'selected' : ''}>${t('set.theme.auto')}</option>
              <option value="night" ${s.theme === 'night' ? 'selected' : ''}>${t('set.theme.night')}</option>
              <option value="day" ${s.theme === 'day' ? 'selected' : ''}>${t('set.theme.day')}</option>
            </select>
          </label>
          <label class="set-row">
            <span>${t('set.units')}</span>
            <select data-set="units">
              <option value="metric" ${s.units === 'metric' ? 'selected' : ''}>${t('set.units.km')}</option>
              <option value="imperial" ${s.units === 'imperial' ? 'selected' : ''}>${t('set.units.mi')}</option>
            </select>
          </label>
          ${this.toggle('buildings3d', t('set.buildings3d'), s.buildings3d)}
          <label class="set-row">
            <span>${t('set.pitch')}</span>
            <span class="set-slider">
              <input type="range" min="0" max="60" step="15" value="${s.cameraPitch}" data-pitch />
              <span class="set-slider__value">${s.cameraPitch}°</span>
            </span>
          </label>
          ${this.toggle('offlineTiles', t('set.offline'), s.offlineTiles)}
          ${this.toggle('logTrips', t('set.logTrips'), s.logTrips)}
        </section>

        ${user?.role === 'admin' ? this.adminSection() : ''}

        <section class="set-group">
          <h3>${t('set.account')}</h3>
          <div class="set-row"><span>${t('set.signedInAs')}</span><strong>${escapeHtml(user?.name ?? '—')}${user?.role === 'admin' ? t('set.admin.tag') : ''}</strong></div>
          <button class="btn btn--ghost" data-act="logout">${t('set.signOut')}</button>
        </section>
      </div>`;

    this.bind();
  }

  /**
   * Every profile is editable now, not just the truck's dimensions - drivers
   * add their own vehicles, so the name and the fuel figure have to be theirs
   * too. Truck dimensions still drive the routing constraints (`max_height`
   * and friends), which is why they stay prominent on a truck profile.
   */
  private vehicleCard(v: VehicleProfile, s: Settings): string {
    const active = v.id === s.activeVehicleId;
    const removable = s.vehicles.length > 1;
    return `
      <div class="vehicle-card ${active ? 'is-active' : ''}" data-vehicle="${v.id}">
        <button class="vehicle-card__pick">
          <strong>${escapeHtml(v.name)}</strong>
          <span>${v.kind === 'truck' ? t('set.vehicle.truck') : t('set.vehicle.car')}</span>
        </button>
        <button class="vehicle-card__edit" data-editvehicle="${v.id}" aria-label="${t('set.vehicle.edit')}">
          <svg viewBox="0 0 24 24"><path d="M4 20 h4 L19 9 a2.1 2.1 0 0 0 -3 -3 L5 17 Z M14 7 l3 3" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linejoin="round" stroke-linecap="round"/></svg>
        </button>
        <div class="vehicle-card__editor" data-editor="${v.id}" hidden>
          <label class="vehicle-field">${t('set.vehicle.name')}
            <input type="text" maxlength="40" data-vname="${v.id}" value="${escapeAttr(v.name)}"/>
          </label>
          <label class="vehicle-field">${t('set.vehicle.kind')}
            <select data-vkind="${v.id}">
              <option value="car" ${v.kind === 'car' ? 'selected' : ''}>${t('set.vehicle.car')}</option>
              <option value="truck" ${v.kind === 'truck' ? 'selected' : ''}>${t('set.vehicle.truck')}</option>
            </select>
          </label>
          <label class="vehicle-field vehicle-field--switch">
            <span>${t('set.easyPass')}</span>
            <span class="switch"><input type="checkbox" data-veasypass="${v.id}" ${v.easyPass ? 'checked' : ''}/><span class="switch__knob"></span></span>
          </label>
          <div class="vehicle-card__dims">
            ${this.dim(v.id, 'height', 'H (m)', v.height)}
            ${this.dim(v.id, 'width', 'W (m)', v.width)}
            ${this.dim(v.id, 'weight', 'Wt (t)', v.weight)}
            ${this.dim(v.id, 'consumption', 'L/100km', v.consumption)}
          </div>
          ${
            removable
              ? `<button class="btn btn--small vehicle-card__delete" data-delvehicle="${v.id}">${t('set.vehicle.delete')}</button>`
              : ''
          }
        </div>
      </div>`;
  }

  private dim(
    vid: string,
    key: 'height' | 'width' | 'weight' | 'consumption',
    label: string,
    value?: number,
  ): string {
    return `<label>${label}<input type="number" step="0.1" min="0" inputmode="decimal"
      data-dim="${vid}:${key}" value="${value ?? ''}"/></label>`;
  }

  /** A cruising-speed row. These drive every ETA the app shows. */
  private speed(key: keyof Settings['speeds'], label: string, value: number): string {
    return `
      <label class="set-row">
        <span>${label}</span>
        <span class="speed-input">
          <input type="number" min="10" max="160" step="5" inputmode="numeric"
                 data-speed="${key}" value="${value}"/>
          <em>km/h</em>
        </span>
      </label>`;
  }

  /**
   * Ferry timetable editor: one block per operator per crossing. Times are a
   * plain comma-separated text field - a driver correcting "the 14:00 is now
   * 14:30" at the pier needs a text box, not a time-picker ceremony.
   */
  private ferriesHtml(): string {
    return loadFerries()
      .map((c) =>
        c.operators
          .map(
            (op) => `
        <div class="ferry-op" data-ferry="${c.id}:${op.id}">
          <div class="set-row"><strong>${c.name} · ${op.name}</strong></div>
          <label class="set-row">
            <span>${t('ferry.truckOk')}</span>
            <span class="switch"><input type="checkbox" data-ferrytruck ${op.truckOk ? 'checked' : ''}/><span class="switch__knob"></span></span>
          </label>
          <label class="set-row">
            <span>${t('ferry.crossingMin')}</span>
            <span class="speed-input">
              <input type="number" min="5" max="600" inputmode="numeric" value="${op.crossingMinutes}" data-ferrycross/>
              <em>min</em>
            </span>
          </label>
          <label class="set-row set-row--stack">
            <span>${c.pierAName} → ${c.pierBName}</span>
            <input type="text" class="ferry-times" value="${op.outbound.join(', ')}" data-ferryout
                   autocomplete="off" spellcheck="false"/>
          </label>
          <label class="set-row set-row--stack">
            <span>${c.pierBName} → ${c.pierAName}</span>
            <input type="text" class="ferry-times" value="${op.inbound.join(', ')}" data-ferryin
                   autocomplete="off" spellcheck="false"/>
          </label>
        </div>`,
          )
          .join(''),
      )
      .join('');
  }

  /*
   * Fleet-message notifications.
   *
   * Not a stored setting: what matters is the browser permission plus whether
   * THIS device holds a push subscription, and neither lives in our settings.
   * Reading the real state avoids a switch that says "on" while the browser has
   * quietly blocked it.
   */
  private pushRow(): string {
    const state = pushPermission();
    if (state === 'unsupported') {
      return `<div class="panel__hint">${t('push.unsupported')}</div>`;
    }
    if (state === 'denied') {
      return `<div class="panel__hint">${t('push.blocked')}</div>`;
    }
    return `
      <label class="set-row">
        <span>${t('push.label')}</span>
        <span class="switch"><input type="checkbox" data-push ${this.pushOn ? 'checked' : ''}/><span class="switch__knob"></span></span>
      </label>
      <div class="panel__hint">${t('push.hint')}</div>`;
  }

  private toggle(path: string, label: string, on: boolean): string {
    return `
      <label class="set-row">
        <span>${label}</span>
        <span class="switch"><input type="checkbox" data-toggle="${path}" ${on ? 'checked' : ''}/><span class="switch__knob"></span></span>
      </label>`;
  }

  private adminSection(): string {
    const users = this.adminUsers;
    const rows =
      users === null
        ? `<div class="panel__hint">${t('set.admin.loading')}</div>`
        : users.length === 0
          ? `<div class="panel__hint">${t('set.admin.none')}</div>`
          : users
              .map(
                (u) => `
        <div class="admin-user" data-uid="${u.id}">
          <span class="admin-user__name">${escapeHtml(u.name)}
            <em class="admin-user__status admin-user__status--${u.status}">${t(`set.status.${u.status}`)}</em>
          </span>
          <span class="admin-user__actions">
            ${
              u.status === 'pending'
                ? `<button class="btn btn--small btn--primary" data-approve="${u.id}">${t('set.admin.approve')}</button>`
                : ''
            }
            ${
              u.status !== 'disabled'
                ? `<button class="btn btn--small btn--ghost" data-disable="${u.id}">${t('set.admin.disable')}</button>`
                : `<button class="btn btn--small btn--ghost" data-approve="${u.id}">${t('set.admin.enable')}</button>`
            }
            <button class="btn btn--small btn--ghost" data-resetpin="${u.id}">${t('set.admin.resetPin')}</button>
          </span>
        </div>`,
              )
              .join('');
    return `<section class="set-group set-group--admin"><h3>${t('set.admin')}</h3>${rows}</section>`;
  }

  private bind(): void {
    this.el.querySelector('[data-act=close]')!.addEventListener('click', () => this.onClose());
    this.el.querySelector('[data-act=logout]')!.addEventListener('click', () => this.onLogout());

    this.el.querySelector<HTMLInputElement>('[data-push]')?.addEventListener('change', (e) => {
      const input = e.currentTarget as HTMLInputElement;
      const want = input.checked;
      input.disabled = true;
      void (want ? enablePush() : disablePush().then(() => false))
        .then((ok) => {
          this.pushOn = want && ok !== false;
          // Permission refused, or the server has no key: put the switch back
          // rather than leaving it claiming something that is not true.
          if (want && !this.pushOn) alert(t('push.failed'));
        })
        .catch((err) => {
          this.pushOn = false;
          alert(err instanceof Error ? err.message : t('push.failed'));
        })
        .finally(() => {
          input.disabled = false;
          void this.open();
        });
    });

    this.el.querySelectorAll<HTMLInputElement>('[data-toggle]').forEach((input) => {
      input.addEventListener('change', () => {
        const path = input.dataset.toggle!;
        if (path.startsWith('route.')) {
          store.patchSettings({ route: { ...store.get().settings.route, [path.slice(6)]: input.checked } });
        } else {
          store.patchSettings({ [path]: input.checked } as Partial<Settings>);
        }
      });
    });

    this.el.querySelectorAll<HTMLInputElement>('[data-speed]').forEach((input) => {
      input.addEventListener('change', () => {
        const key = input.dataset.speed as keyof Settings['speeds'];
        const n = Number(input.value);
        // Clamp rather than reject: a stray keystroke must not produce a NaN
        // that silently poisons every ETA in the app.
        const value = Number.isFinite(n) ? Math.min(160, Math.max(10, Math.round(n))) : 60;
        input.value = String(value);
        store.patchSettings({ speeds: { ...store.get().settings.speeds, [key]: value } });
      });
    });

    this.el.querySelectorAll<HTMLElement>('[data-sound]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const mode = btn.dataset.sound as Settings['soundMode'];
        store.patchSettings({ soundMode: mode });
        // Hearing the change is the only way to know it took - especially for
        // 'alerts', where there is nothing to hear until a turn comes up.
        if (mode !== 'muted') chime(store.get().settings.voiceVolume);
        this.render();
      });
    });

    this.el.querySelectorAll<HTMLInputElement>('[data-volume]').forEach((input) => {
      const readout = input.parentElement?.querySelector('.set-slider__value');
      input.addEventListener('input', () => {
        if (readout) readout.textContent = `${input.value}%`;
      });
      input.addEventListener('change', () => {
        store.patchSettings({ voiceVolume: Number(input.value) / 100 });
        chime(store.get().settings.voiceVolume);
      });
    });

    this.el.querySelectorAll<HTMLInputElement>('[data-pitch]').forEach((input) => {
      const readout = input.parentElement?.querySelector('.set-slider__value');
      input.addEventListener('input', () => {
        if (readout) readout.textContent = `${input.value}°`;
      });
      // The map tilts behind the panel on release - see MapView.setCameraPrefs.
      input.addEventListener('change', () => {
        store.patchSettings({ cameraPitch: Number(input.value) as Settings['cameraPitch'] });
      });
    });

    this.el.querySelectorAll<HTMLInputElement>('[data-drivezoom]').forEach((input) => {
      const readout = input.parentElement?.querySelector('.set-slider__value');
      // Slider position 0 is auto; 1..8 map to zoom 14..17.5 in half steps.
      const toZoom = (i: number): number => (i <= 0 ? 0 : 13.5 + i * 0.5);
      input.addEventListener('input', () => {
        const z = toZoom(Number(input.value));
        if (readout) readout.textContent = z === 0 ? t('set.driveZoom.auto') : z.toFixed(1);
      });
      input.addEventListener('change', () => {
        store.patchSettings({ driveZoom: toZoom(Number(input.value)) });
      });
    });

    this.el.querySelectorAll<HTMLElement>('[data-act=test-sound]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const s2 = store.get().settings;
        if (s2.soundMode === 'voice') announce(t('sound.voiceHint'), 'action');
        else chime(s2.voiceVolume);
      });
    });

    // Ferry timetable edits: parse, normalise back into the field, persist.
    // Invalid time text leaves the stored schedule untouched rather than
    // wiping it - a fat-fingered edit at the pier must not delete the day.
    this.el.querySelectorAll<HTMLElement>('.ferry-op').forEach((box) => {
      const [cid, oid] = (box.dataset.ferry ?? '').split(':');
      const apply = () => {
        const ferries = loadFerries();
        const op = ferries.find((c) => c.id === cid)?.operators.find((o) => o.id === oid);
        if (!op) return;
        const truck = box.querySelector<HTMLInputElement>('[data-ferrytruck]');
        const cross = box.querySelector<HTMLInputElement>('[data-ferrycross]');
        const out = box.querySelector<HTMLInputElement>('[data-ferryout]');
        const inn = box.querySelector<HTMLInputElement>('[data-ferryin]');
        if (truck) op.truckOk = truck.checked;
        const mins = Number(cross?.value);
        if (Number.isFinite(mins) && mins > 0) op.crossingMinutes = Math.round(mins);
        if (out) {
          const parsed = parseTimes(out.value);
          if (parsed) op.outbound = parsed;
          out.value = op.outbound.join(', ');
        }
        if (inn) {
          const parsed = parseTimes(inn.value);
          if (parsed) op.inbound = parsed;
          inn.value = op.inbound.join(', ');
        }
        saveFerries(ferries);
      };
      box.querySelectorAll('input').forEach((inp) => inp.addEventListener('change', apply));
    });

    this.el.querySelectorAll<HTMLSelectElement>('[data-set]').forEach((sel) => {
      sel.addEventListener('change', () => {
        const key = sel.dataset.set as keyof Settings;
        const value =
          key === 'speedWarnOffset' || key === 'signalSeconds' || key === 'roughSensitivity'
            ? Number(sel.value)
            : sel.value;
        store.patchSettings({ [key]: value } as Partial<Settings>);
        // Language touches every rendered string - a clean reload is the
        // simplest correct way to apply it everywhere at once. The service
        // worker gets its own copy first: it renders push notifications with
        // no page running, so it cannot read the setting when the time comes.
        if (key === 'language') void mirrorLangForWorker().finally(() => location.reload());
      });
    });

    this.el.querySelectorAll<HTMLElement>('.vehicle-card__pick').forEach((btn) => {
      btn.addEventListener('click', () => {
        const id = (btn.closest('.vehicle-card') as HTMLElement).dataset.vehicle!;
        store.patchSettings({ activeVehicleId: id });
        this.render();
      });
    });

    /* ---------------------------- vehicle CRUD --------------------------- */

    this.el.querySelectorAll<HTMLButtonElement>('[data-editvehicle]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const id = btn.dataset.editvehicle!;
        const editor = this.el.querySelector<HTMLElement>(`[data-editor="${cssEscape(id)}"]`);
        if (editor) editor.hidden = !editor.hidden;
        btn.classList.toggle('is-open', editor ? !editor.hidden : false);
      });
    });

    this.el.querySelectorAll<HTMLButtonElement>('[data-addvehicle]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const v = newVehicle(btn.dataset.addvehicle as VehicleKind);
        const vehicles = [...store.get().settings.vehicles, v];
        store.patchSettings({ vehicles, activeVehicleId: v.id });
        this.render();
        // Open the new profile straight away - it needs a name.
        const editor = this.el.querySelector<HTMLElement>(`[data-editor="${cssEscape(v.id)}"]`);
        if (editor) editor.hidden = false;
        this.el.querySelector<HTMLInputElement>(`[data-vname="${cssEscape(v.id)}"]`)?.focus();
      });
    });

    this.el.querySelectorAll<HTMLButtonElement>('[data-delvehicle]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const id = btn.dataset.delvehicle!;
        const s = store.get().settings;
        if (s.vehicles.length <= 1) return; // never orphan the picker
        const vehicles = s.vehicles.filter((v) => v.id !== id);
        const activeVehicleId = s.activeVehicleId === id ? vehicles[0].id : s.activeVehicleId;
        store.patchSettings({ vehicles, activeVehicleId });
        this.render();
      });
    });

    this.el.querySelectorAll<HTMLInputElement>('[data-vname]').forEach((input) => {
      input.addEventListener('change', () => {
        const id = input.dataset.vname!;
        const name = input.value.trim() || t('set.vehicle.unnamed');
        const vehicles = store.get().settings.vehicles.map((v) =>
          v.id === id ? { ...v, name } : v,
        );
        store.patchSettings({ vehicles });
        this.render();
      });
    });

    this.el.querySelectorAll<HTMLInputElement>('[data-veasypass]').forEach((input) => {
      input.addEventListener('change', () => {
        const id = input.dataset.veasypass!;
        const vehicles = store.get().settings.vehicles.map((v) =>
          v.id === id ? { ...v, easyPass: input.checked } : v,
        );
        store.patchSettings({ vehicles });
      });
    });

    this.el.querySelectorAll<HTMLSelectElement>('[data-vkind]').forEach((sel) => {
      sel.addEventListener('change', () => {
        const id = sel.dataset.vkind!;
        const kind = sel.value as VehicleKind;
        const vehicles = store.get().settings.vehicles.map((v) =>
          v.id === id ? { ...v, kind } : v,
        );
        store.patchSettings({ vehicles });
        this.render();
      });
    });

    this.el.querySelectorAll<HTMLInputElement>('[data-dim]').forEach((input) => {
      input.addEventListener('change', () => {
        const [vid, key] = input.dataset.dim!.split(':') as [
          string,
          'height' | 'width' | 'weight' | 'consumption',
        ];
        const value = input.value === '' ? undefined : Number(input.value);
        const vehicles = store.get().settings.vehicles.map((v) =>
          v.id === vid ? { ...v, [key]: value } : v,
        );
        store.patchSettings({ vehicles });
      });
    });

    /* admin actions */
    this.el.querySelectorAll<HTMLButtonElement>('[data-approve]').forEach((b) =>
      b.addEventListener('click', () => void this.setStatus(Number(b.dataset.approve), 'approved')),
    );
    this.el.querySelectorAll<HTMLButtonElement>('[data-disable]').forEach((b) =>
      b.addEventListener('click', () => void this.setStatus(Number(b.dataset.disable), 'disabled')),
    );
    this.el.querySelectorAll<HTMLButtonElement>('[data-resetpin]').forEach((b) =>
      b.addEventListener('click', () => {
        const pin = prompt(t('set.admin.newPin'));
        if (!pin || !/^\d{4,10}$/.test(pin)) return;
        void api
          .adminResetPin(Number(b.dataset.resetpin), pin)
          .then(() => this.refreshAdmin())
          .catch((e) => alert(e.message));
      }),
    );
  }

  private async setStatus(userId: number, status: 'approved' | 'disabled'): Promise<void> {
    try {
      await api.adminSetStatus(userId, status);
      await this.refreshAdmin();
    } catch (e) {
      alert(e instanceof Error ? e.message : t('set.failed'));
    }
  }

  private async refreshAdmin(): Promise<void> {
    this.adminUsers = (await api.adminListUsers()).users;
    this.render();
  }
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
}

function escapeAttr(s: string): string {
  return escapeHtml(s);
}

/** Vehicle ids are generated, but a selector still must not break on them. */
function cssEscape(s: string): string {
  return typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(s) : s.replace(/["\\]/g, '\\$&');
}
